import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'
import { useAuthStore } from '../stores/auth'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'landing',
    path: '/',
    component: () => import('../pages/landing/LandingPage.vue'),
  },
  {
    name: 'terms',
    path: '/terms',
    component: () => import('../pages/legal/TermsOfUse.vue'),
  },
  {
    name: 'privacy',
    path: '/privacy',
    component: () => import('../pages/legal/PrivacyPolicy.vue'),
  },
  {
    name: 'cookies',
    path: '/cookies',
    component: () => import('../pages/legal/CookiePolicy.vue'),
  },
  {
    name: 'sitemap',
    path: '/sitemap',
    component: () => import('../pages/legal/SiteMap.vue'),
  },
  {
    // Homeowners verify inline via an emailed 6-digit code on this same page — there is no
    // separate verify landing route anymore.
    name: 'join',
    path: '/join/:code',
    component: () => import('../pages/join/JoinPage.vue'),
  },
  {
    name: 'admin',
    path: '/app',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'households',
        path: 'households',
        component: () => import('../pages/households/HouseholdsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'household-detail',
        path: 'households/:id',
        component: () => import('../pages/households/HouseholdDetailPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'streets',
        path: 'streets',
        component: () => import('../pages/streets/StreetsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'buildings',
        path: 'buildings',
        component: () => import('../pages/buildings/BuildingsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'household-types',
        path: 'household-types',
        component: () => import('../pages/household-types/HouseholdTypesPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'amenities',
        path: 'amenities',
        component: () => import('../pages/amenities/AmenitiesPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'announcements',
        path: 'announcements',
        component: () => import('../pages/announcements/AnnouncementsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'] },
      },
      {
        name: 'incidents',
        path: 'incidents',
        component: () => import('../pages/incidents/IncidentsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'] },
      },
      {
        name: 'stickers',
        path: 'stickers',
        component: () => import('../pages/stickers/StickersPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/ReportsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'audit-annual',
        path: 'audit-annual',
        component: () => import('../pages/audit-reports/AnnualAuditPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'member-ledger',
        path: 'member-ledger',
        component: () => import('../pages/audit-reports/MemberLedgerPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'credit-ledger',
        path: 'credit-ledger',
        component: () => import('../pages/credits/CreditLedgerPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'audit-logs',
        path: 'audit-logs',
        component: () => import('../pages/audit-logs/AuditLogsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'visitor-passes',
        path: 'visitor-passes',
        component: () => import('../pages/visitor-passes/VisitorPassesPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'contact-inquiries',
        path: 'contact-inquiries',
        component: () => import('../pages/contact-inquiries/ContactInquiriesPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] },
      },
      {
        name: 'tenants',
        path: 'tenants',
        component: () => import('../pages/tenants/TenantsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] },
      },
      {
        name: 'email-templates',
        path: 'email-templates',
        component: () => import('../pages/platform/EmailTemplatesPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] },
      },
      {
        name: 'invite-homeowners',
        path: 'invite-homeowners',
        component: () => import('../pages/admin/invite/InviteHomeownersPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'] },
      },
      {
        name: 'plans',
        path: 'plans',
        component: () => import('../pages/plans/PlansPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] },
      },
      {
        name: 'subscriptions',
        path: 'subscriptions',
        component: () => import('../pages/subscriptions/SubscriptionsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN'] },
      },
      {
        name: 'subscription',
        path: 'subscription',
        component: () => import('../pages/subscription/SubscriptionBillingPage.vue'),
        meta: { requiresAuth: true, roles: ['ASSOCIATION_ADMIN'] },
      },
      {
        name: 'homeowner-dashboard',
        path: 'my-dashboard',
        component: () => import('../pages/homeowner/HomeownerDashboard.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'] },
      },
      {
        name: 'guard-home',
        path: 'guard-home',
        component: () => import('../pages/guard/GuardHomePage.vue'),
        // Guard-only: the hub is the guard's task launcher (two of its tiles route to
        // GUARD-only pages). Admins have their own nav/dashboard.
        meta: { requiresAuth: true, roles: ['GUARD'] },
      },
      {
        name: 'guard-gate',
        path: 'gate-lookup',
        component: () => import('../pages/guard/GateLookupPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-visitors',
        path: 'visitor-logs',
        component: () => import('../pages/guard/VisitorRequestsPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-visitor-requests',
        path: 'visitor-requests',
        component: () => import('../pages/guard/VisitorRequestsPage.vue'),
        meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-visitor-directory',
        path: 'visitors',
        component: () => import('../pages/guard/VisitorsPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-deliveries',
        path: 'delivery-logs',
        component: () => import('../pages/guard/DeliveryLogsPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-visitor-pass-scan',
        path: 'visitor-pass-scan',
        component: () => import('../pages/guard/VisitorPassScanPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD', 'SUPER_ADMIN', 'ASSOCIATION_ADMIN'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'guard-announcements',
        path: 'staff-announcements',
        component: () => import('../pages/homeowner/MyAnnouncementsPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD'] },
      },
      {
        name: 'guard-incidents',
        path: 'incident-log',
        component: () => import('../pages/guard/GuardIncidentsPage.vue'),
        meta: { requiresAuth: true, roles: ['GUARD'] },
      },
      {
        name: 'homeowner-bills',
        path: 'my-bills',
        component: () => import('../pages/homeowner/MyBillsPage.vue'),
        // Hits BillingController (gated) via useBillingStore, so it's gated too even
        // though it isn't literally "my-payments".
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-bookings',
        path: 'my-bookings',
        component: () => import('../pages/homeowner/MyBookingsPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'] },
      },
      {
        name: 'homeowner-announcements',
        path: 'my-announcements',
        component: () => import('../pages/homeowner/MyAnnouncementsPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'] },
      },
      {
        name: 'homeowner-incidents',
        path: 'my-incidents',
        component: () => import('../pages/homeowner/MyIncidentsPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'] },
      },
      {
        name: 'homeowner-payments',
        path: 'my-payments',
        component: () => import('../pages/homeowner/MyPaymentsPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-credits',
        path: 'my-credits',
        component: () => import('../pages/credits/CreditLedgerPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-stickers',
        path: 'my-stickers',
        component: () => import('../pages/homeowner/MyStickersPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-vehicles',
        path: 'my-vehicles',
        component: () => import('../pages/homeowner/MyVehiclesPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-visitors',
        path: 'my-visitors',
        component: () => import('../pages/homeowner/VisitorCenterPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-visitor-requests',
        path: 'my-visitor-requests',
        component: () => import('../pages/homeowner/VisitorCenterPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-deliveries',
        path: 'my-deliveries',
        component: () => import('../pages/homeowner/MyDeliveryLogsPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'homeowner-visitor-passes',
        path: 'my-visitor-passes',
        component: () => import('../pages/homeowner/MyVisitorPassesPage.vue'),
        meta: { requiresAuth: true, roles: ['HOMEOWNER'], requiresPlan: 'COMMUNITY' },
      },
      {
        name: 'payments',
        path: 'payments',
        component: RouteViewComponent,
        redirect: { name: 'payment-methods' },
        meta: { requiresAuth: true },
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
            meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingHomePage.vue'),
            meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
          },
          {
            name: 'billing-household',
            path: 'billing/household/:id',
            component: () => import('../pages/billing/BillingHouseholdPage.vue'),
            meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ASSOCIATION_ADMIN', 'BOARD_AUDITOR'], requiresPlan: 'COMMUNITY' },
          },
        ],
      },
      {
        // Landing spot for gated-route redirects (see beforeEach below). No `roles`
        // restriction — any authenticated role can land here regardless of which
        // gated page they came from.
        name: 'upgrade',
        path: 'upgrade',
        component: () => import('../pages/UpgradeLocked.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/portal',
    redirect: { name: 'login' },
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/RegisterAssociation.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const role = authStore.user?.role
  const allowedRoles = to.matched
    .flatMap((record) => (record.meta.roles as string[] | undefined) ?? [])
    .filter((value, index, self) => self.indexOf(value) === index)

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  const fallbackByRole: Record<string, string> = {
    SUPER_ADMIN: 'dashboard',
    ASSOCIATION_ADMIN: 'dashboard',
    BOARD_AUDITOR: 'reports',
    HOMEOWNER: 'homeowner-dashboard',
    GUARD: 'guard-home',
  }

  if (requiresAuth && allowedRoles.length > 0 && (!role || !allowedRoles.includes(role))) {
    if (!role) {
      next({ name: 'login' })
      return
    }
    next({ name: fallbackByRole[role] ?? 'login' })
    return
  }

  // Community-plan gate: a locked (free) tenant is redirected to the Upgrade-locked page
  // for any route flagged requiresPlan==='COMMUNITY'. SUPER_ADMIN always bypasses (mirrors
  // the backend's [RequiresCommunityPlan] bypass). The 'upgrade' route itself carries no
  // requiresPlan meta, so this can't loop.
  const requiresPlan = to.matched.some((record) => record.meta.requiresPlan === 'COMMUNITY')
  if (requiresPlan && !authStore.isSuperAdmin && !authStore.featuresUnlocked) {
    next({ name: 'upgrade' })
    return
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: fallbackByRole[role ?? ''] ?? 'dashboard' })
    return
  }

  next()
})

export default router
